// Whiteboard — the pictures on a board, in this browser. Each one is fetched
// once (with the session's credentials), kept as an object URL for the
// screen and as a decoded image for the PNG / PDF exports. A picture placed
// here is shown at once and uploaded in the background; its element is held
// back from the saver until the upload has landed (sync.js).

import whiteboardApi from '@modules/database/whiteboard/frontend/api';

const MAX_SIDE = 8192;            // a bigger photo is scaled down before it is placed
const MAX_BYTES = 20 * 1024 * 1024;

function decode(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('The picture could not be read.'));
    img.src = url;
  });
}

async function toBlob(canvas, type, quality) {
  return new Promise((r) => canvas.toBlob(r, type, quality));
}

// Make a picture fit to be placed: SVG → PNG, a huge photo → smaller.
async function prepare(file) {
  let blob = file; let type = file.type;
  const url = URL.createObjectURL(file);
  let img;
  try { img = await decode(url); } finally { URL.revokeObjectURL(url); }
  let w = img.naturalWidth || img.width || 800; let h = img.naturalHeight || img.height || 600;
  const isSvg = type === 'image/svg+xml';
  const tooBig = Math.max(w, h) > MAX_SIDE || file.size > MAX_BYTES;
  const supported = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(type);
  if (isSvg || tooBig || !supported) {
    const k = isSvg ? Math.min(4, MAX_SIDE / Math.max(w, h), Math.max(1, 2000 / Math.max(w, h))) : Math.min(1, MAX_SIDE / Math.max(w, h));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    const keepJpeg = type === 'image/jpeg';
    type = keepJpeg ? 'image/jpeg' : 'image/png';
    blob = await toBlob(c, type, 0.92);
    if (blob && blob.size > MAX_BYTES && type === 'image/png') { type = 'image/jpeg'; blob = await toBlob(c, type, 0.9); }
    w = c.width; h = c.height;
  }
  if (!blob) throw new Error('The picture could not be prepared.');
  return { blob, type, w, h };
}

export class FileCache {
  constructor() {
    this.files = new Map();   // id → { status: 'loading'|'ready'|'uploading'|'failed', url, img, blob, mime, width, height, error }
    this.listeners = new Set();
    this.rev = 0;
  }
  subscribe(fn) { this.listeners.add(fn); return () => this.listeners.delete(fn); }
  emit() { this.rev += 1; for (const fn of this.listeners) fn(); }
  get(id) { return this.files.get(id) || null; }
  urlOf(id) { return this.files.get(id)?.url || null; }
  // May the saver send an element yet? (an image waits for its upload)
  ready(el) {
    if (el.deleted || el.type !== 'image') return true;
    const f = this.files.get(el.fileId);
    return !f || f.status !== 'uploading';
  }
  failed(el) { return el.type === 'image' && this.files.get(el.fileId)?.status === 'failed'; }

  // A picture someone placed: fetch it once.
  ensure(id) {
    if (!id || this.files.has(id)) return;
    this.files.set(id, { status: 'loading' });
    whiteboardApi.file(id).then(async (blob) => {
      const url = URL.createObjectURL(blob);
      let img = null;
      try { img = await decode(url); } catch { /* drawn as a grey box */ }
      this.files.set(id, { status: 'ready', url, img, blob, mime: blob.type, width: img?.naturalWidth, height: img?.naturalHeight });
      this.emit();
    }).catch((e) => {
      this.files.set(id, { status: 'failed', error: e?.message || 'Not found' });
      this.emit();
      // try again later (a picture may still be uploading on someone else's screen)
      setTimeout(() => { if (this.files.get(id)?.status === 'failed') { this.files.delete(id); this.ensure(id); } }, 8000);
    });
  }

  // A picture placed HERE: shown at once, uploaded in the background.
  // → { id, w, h } (its natural size, for the element)
  async place(file, { id, key, fieldId, onUploaded, onFailed }) {
    const prepared = await prepare(file);
    const url = URL.createObjectURL(prepared.blob);
    let img = null;
    try { img = await decode(url); } catch { /* keep the URL */ }
    this.files.set(id, { status: 'uploading', url, img, blob: prepared.blob, mime: prepared.type, width: prepared.w, height: prepared.h });
    this.emit();
    whiteboardApi.upload(key, fieldId, { id, blob: prepared.blob, name: file.name, width: prepared.w, height: prepared.h })
      .then(() => { const f = this.files.get(id); if (f) { this.files.set(id, { ...f, status: 'ready' }); this.emit(); } onUploaded?.(); })
      .catch((e) => {
        const msg = e?.response?.data?.error || e?.message || 'The upload failed.';
        const f = this.files.get(id); if (f) this.files.set(id, { ...f, status: 'failed', error: msg });
        this.emit(); onFailed?.(msg);
      });
    return { id, w: prepared.w, h: prepared.h };
  }

  dispose() {
    for (const f of this.files.values()) if (f.url) URL.revokeObjectURL(f.url);
    this.files.clear();
  }
}
